<template>
  <div class="invoice-price-summary text-right row justify-end">
    <table>
      <tr v-if="discount !== null">
        <td class="text-dark q-pr-md" :class="{ 'q-pb-md': !this.cashDiscount }">{{ discountText }}:</td>
        <td class="text-bold text-dark" :class="{ 'q-pb-md': !this.cashDiscount }">
          {{ discountInCurrency | inCurrency }}
        </td>
      </tr>
      <tr v-if="cashDiscount !== null">
        <td class="text-dark q-pr-md q-pb-md">{{ $t('cash_discount') }}:</td>
        <td class="text-bold text-dark q-pb-md" v-html="cashDiscountText"></td>
      </tr>
      <tr>
        <td class="text-dark q-pr-md">{{ $t('net') }}:</td>
        <td class="text-bold text-dark">{{ net | inCurrency }}</td>
      </tr>
      <tr v-for="(rate, index) in salesTaxRates" :key="index">
        <td class="text-dark q-pr-md">{{ $t('sales_tax') }} {{ rate.tax }}%:</td>
        <td class="text-bold text-dark">{{ rate.value | inCurrency }}</td>
      </tr>
      <tr>
        <td class="text-dark q-pr-md">{{ $t('gross') }}:</td>
        <td class="text-bold text-dark">{{ gross | inCurrency }}</td>
      </tr>
    </table>
  </div>
</template>

<script>
  import { formatMoney, formatPercentage } from 'src/utils/format'
  import Decimal from 'decimal.js'
  import { date } from 'quasar'

  function numberToDecimal (number) {
    if (typeof number === 'string') {
      number = number.replace(/,/, '.')
    }

    return new Decimal(number)
  }

  export default {
    props: {
      items: {
        type: Array,
        required: true
      },
      discount: {
        required: true
      },
      cashDiscount: {
        required: true
      }
    },

    computed: {
      netWithoutDiscount () {
        return this.items.reduce((acc, item) => {
          return acc.plus(this.calculateNetForItem(item))
        }, new Decimal(0))
      },
      netWithDiscount () {
        if (!this.hasDiscount) {
          return this.netWithoutDiscount
        }

        const discount = numberToDecimal(this.discount.value)

        if (this.discountIsRelative) {
          const value = new Decimal(100).minus(discount).dividedBy(100)
          return this.netWithoutDiscount.times(value)
        } else {
          return this.netWithoutDiscount.minus(discount)
        }
      },
      net () {
        return this.hasDiscount ? this.netWithDiscount : this.netWithoutDiscount
      },
      gross () {
        return this.net.plus(this.totalSalesTax)
      },
      totalSalesTax () {
        return this.salesTaxRates.reduce((acc, salesTaxRate) => {
          return acc.plus(salesTaxRate.value)
        }, new Decimal(0))
      },
      salesTaxRates () {
        const rates = this.items.reduce((acc, item) => {
          if (!item.salesTax || item.salesTax <= 0) {
            return acc
          }

          const salesTax = item.salesTax

          let salesTaxIndex = acc.findIndex(item => item.tax === salesTax)

          if (salesTaxIndex === -1) {
            acc.push({
              tax: salesTax,
              value: new Decimal(0)
            })

            salesTaxIndex = acc.length - 1
          }

          acc.splice(salesTaxIndex, 1, {
            tax: acc[salesTaxIndex].tax,
            value: acc[salesTaxIndex].value.plus(this.calculateNetForItem(item))
          })

          return acc
        }, [])

        const relativeDiscount = this.calculatedRelativeDiscount

        return rates.map(item => {
          const discountedTotal = new Decimal(item.value).mul(
            new Decimal(1).sub(relativeDiscount)
          )

          const discountedSalesTax = new Decimal(discountedTotal)
            .mul(new Decimal(item.tax).div(new Decimal(100)))

          return {
            tax: item.tax,
            value: discountedSalesTax.toFixed(2, Decimal.ROUND_UP)
          }
        })
      },
      discountInCurrency () {
        return this.netWithoutDiscount.minus(this.netWithDiscount)
      },
      hasDiscount () {
        return this.discount != null
      },
      discountIsRelative () {
        return this.hasDiscount && this.discount.type === 'rel'
      },
      calculatedRelativeDiscount () {
        if (this.discountIsRelative) {
          return new Decimal(this.discount.value)
            .div(new Decimal(100))
        }

        return new Decimal(this.hasDiscount ? this.discount.value : 0).div(
          new Decimal(!this.netWithoutDiscount.isZero() ? this.netWithoutDiscount : 1)
        )
      },
      discountText () {
        if (this.discountIsRelative) {
          const discount = formatPercentage(new Decimal(this.discount.value).div(100))

          return this.$t('discount') + ` (${discount})`
        }

        return this.$t('discount')
      },
      cashDiscountText () {
        const dateLimit = date.formatDate(this.cashDiscount.dateLimit, this.$t('date_format'))
        return `${this.cashDiscount.amount}&nbsp;%&nbsp;/&nbsp;${dateLimit}`
      }
    },

    methods: {
      calculateNetForItem (item) {
        let itemNet = null

        if (item.price !== null && item.quantity !== null) {
          let itemPrice = numberToDecimal(item.price)
          let itemQuantity = numberToDecimal(item.quantity)
          itemNet = itemPrice.times(itemQuantity)
        } else {
          itemNet = new Decimal(0)
        }

        if (item.discountRelative) {
          const discount = numberToDecimal(item.discountRelative)
          const value = new Decimal(1).minus(discount.dividedBy(100))
          itemNet = itemNet.times(value)
        }

        if (item.discountAbsolute) {
          const discount = numberToDecimal(item.discountAbsolute)
          itemNet = itemNet.minus(discount)
        }

        return itemNet.toFixed(0)
      }
    },

    filters: {
      inCurrency: formatMoney
    }
  }
</script>

<style lang="stylus">
  .invoice-price-summary
    margin-left: auto
    padding: 1rem
    width: 30%
    min-width: 250px
</style>
